import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiSave,
  FiX,
  FiImage,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiClock,
  FiEdit3,
} from "react-icons/fi";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { uploadAPI } from "../../services/api";
import toast from "react-hot-toast";
import IngredientSearch from "../../components/IngredientSearch";

const MealPlanFormContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const MealPlanFormContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageDescription = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  p {
    color: #666;
    font-size: 1.125rem;
  }
`;

const FormCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #333;
  }

  input,
  select,
  textarea {
    padding: 0.75rem;
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2d5016;
    }

    &.error {
      border-color: #dc3545;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e9ecef;
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    margin: 0;
    font-size: 0.875rem;
    font-weight: 500;
  }
`;

const FulfillmentSection = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  background: #fafbfc;
`;

const ImageUpload = styled.div`
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2d5016;
    background: #f8f9fa;
  }

  .upload-icon {
    font-size: 2rem;
    color: #666;
    margin-bottom: 1rem;
  }

  .upload-text {
    color: #666;
    margin-bottom: 0.5rem;
  }

  .upload-hint {
    font-size: 0.75rem;
    color: #999;
  }

  input[type="file"] {
    display: none;
  }
`;

const MenuItemsSection = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }
`;

const MenuItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: 1px solid #e0e0e0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MenuItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h4 {
    font-size: 1rem;
    font-weight: 600;
    color: #2d5016;
    margin: 0;
  }
`;

const MenuItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MenuItemFullRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NutritionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RemoveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const MenuItemInput = styled.input`
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const MenuItemTextarea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: border-color 0.3s ease;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const MenuItemLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25rem;
`;

const RemoveButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #dc3545;
  background: white;
  color: #dc3545;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #dc3545;
    color: white;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #2d5016;
  background: white;
  color: #2d5016;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;

  &:hover {
    background: #2d5016;
    color: white;
  }
`;

const FormFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;
`;

const TabButton = styled.button`
  padding: 1rem 1.5rem;
  border: none;
  background: ${(props) => (props.active ? "#2d5016" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#666")};
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#2d5016" : "transparent")};

  &:hover {
    background: ${(props) => (props.active ? "#2d5016" : "#f8f9fa")};
    color: ${(props) => (props.active ? "white" : "#2d5016")};
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#f8f9fa")};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Helper function to convert day numbers (0-6) to day names
// 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
const convertDayNumberToName = (dayNumber) => {
  if (dayNumber === null || dayNumber === undefined || dayNumber === "") {
    return "";
  }
  const dayMap = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };
  // Handle both string and number inputs
  const num = typeof dayNumber === "string" ? parseInt(dayNumber, 10) : dayNumber;
  return dayMap[num] !== undefined ? dayMap[num] : "";
};

const schema = yup.object().shape({
  title: yup.string().required("Meal plan title is required"),
  description: yup.string().required("Description is required"),
  basePrice: yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty string or invalid values
      if (originalValue === "" || originalValue == null) {
        return undefined; // Return undefined to trigger required error
      }
      const parsed = parseFloat(originalValue);
      return isNaN(parsed) ? undefined : parsed;
    })
    .positive("Base price must be positive")
    .required("Base price is required"),
  deliveryFee: yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty string or invalid values
      if (originalValue === "" || originalValue == null) {
        return 0;
      }
      const parsed = parseFloat(originalValue);
      return isNaN(parsed) ? 0 : parsed;
    })
    .min(0, "Delivery fee must be 0 or positive")
    .required("Delivery fee is required"),
  planType: yup.string().required("Plan type is required"),
  durationDays: yup
    .number()
    .positive("Duration must be positive")
    .required("Duration is required"),
  maxSubscribers: yup
    .number()
    .positive("Max subscribers must be positive")
    .required("Max subscribers is required"),
});

const MealPlanForm = ({
  mealPlan = null,
  onSave,
  onCancel,
  isAdmin = false,
  selectedVendorId = null,
  onVendorChange = null,
  vendors = [],
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const [vendorId, setVendorId] = useState(selectedVendorId || null);
  const [menuItems, setMenuItems] = useState([
    {
      day: 1,
      meal: "",
      description: "",
      ingredients: [], // Changed to array for ingredient objects
      // allergens removed - auto-detected from ingredients
      calories: "",
      protein: "",
      carbs: "",
      fat: "",
    },
    {
      day: 2,
      meal: "",
      description: "",
      ingredients: [], // Changed to array for ingredient objects
      // allergens removed - auto-detected from ingredients
      calories: "",
      protein: "",
      carbs: "",
      fat: "",
    },
    {
      day: 3,
      meal: "",
      description: "",
      ingredients: [], // Changed to array for ingredient objects
      // allergens removed - auto-detected from ingredients
      calories: "",
      protein: "",
      carbs: "",
      fat: "",
    },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(
    mealPlan?.imageUrl || null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [offersDelivery, setOffersDelivery] = useState(
    mealPlan?.offers_delivery ?? mealPlan?.offersDelivery ?? true
  );
  const [offersPickup, setOffersPickup] = useState(
    mealPlan?.offers_pickup ?? mealPlan?.offersPickup ?? false
  );
  const [pickupAddress, setPickupAddress] = useState(
    mealPlan?.pickup_address || mealPlan?.pickupAddress || ""
  );
  const [pickupInstructions, setPickupInstructions] = useState(
    mealPlan?.pickup_instructions || mealPlan?.pickupInstructions || ""
  );
  const [basePrice, setBasePrice] = useState(
    mealPlan?.base_price || mealPlan?.basePrice || mealPlan?.price || ""
  );
  const [deliveryFee, setDeliveryFee] = useState(
    mealPlan?.delivery_fee ||
      mealPlan?.deliveryFee ||
      mealPlan?.deliveryPrice ||
      ""
  );
  const [freeDelivery, setFreeDelivery] = useState(
    mealPlan?.delivery_fee === 0 ||
      mealPlan?.delivery_fee === "0" ||
      mealPlan?.deliveryFee === 0 ||
      mealPlan?.deliveryFee === "0" ||
      false
  );
  const [pickupLatitude, setPickupLatitude] = useState(
    mealPlan?.pickup_latitude
      ? parseFloat(mealPlan.pickup_latitude)
      : mealPlan?.pickupLatitude
        ? parseFloat(mealPlan.pickupLatitude)
        : null
  );
  const [pickupLongitude, setPickupLongitude] = useState(
    mealPlan?.pickup_longitude
      ? parseFloat(mealPlan.pickup_longitude)
      : mealPlan?.pickupLongitude
        ? parseFloat(mealPlan.pickupLongitude)
        : null
  );
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Default cancel behavior - navigate back to meal plans management
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/vendor/meal-plans");
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: mealPlan?.title || "",
      description: mealPlan?.description || "",
      basePrice: mealPlan?.basePrice || mealPlan?.price || "",
      deliveryFee: mealPlan?.deliveryFee || mealPlan?.deliveryPrice || "",
      planType: mealPlan?.planType || "weekly",
      durationDays: mealPlan?.durationDays || 7,
      maxSubscribers: mealPlan?.maxSubscribers || 50,
    },
  });

  const planType = watch("planType");

  // Update form fields when mealPlan changes (for data persistence)
  useEffect(() => {
    if (mealPlan) {
      // Update form values using setValue
      setValue("title", mealPlan.title || "");
      setValue("description", mealPlan.description || "");
      setValue("basePrice", mealPlan.basePrice || mealPlan.base_price || mealPlan.price || "");
      setValue("deliveryFee", mealPlan.deliveryFee || mealPlan.delivery_fee || mealPlan.deliveryPrice || "");
      setValue("planType", mealPlan.planType || mealPlan.plan_type || "weekly");
      setValue("durationDays", mealPlan.durationDays || mealPlan.duration_days || 7);
      setValue("maxSubscribers", mealPlan.maxSubscribers || mealPlan.max_subscribers || 50);

      // Update state values
      setOffersDelivery(
        mealPlan.offers_delivery === true ||
        mealPlan.offers_delivery === "true" ||
        mealPlan.offers_delivery === 1 ||
        mealPlan.offersDelivery === true ||
        mealPlan.offersDelivery === "true" ||
        mealPlan.offersDelivery === 1 ||
        mealPlan.offersDelivery === true ||
        true // Default to true
      );
      setOffersPickup(
        mealPlan.offers_pickup === true ||
        mealPlan.offers_pickup === "true" ||
        mealPlan.offers_pickup === 1 ||
        mealPlan.offersPickup === true ||
        mealPlan.offersPickup === "true" ||
        mealPlan.offersPickup === 1 ||
        false
      );
      setPickupAddress(mealPlan.pickup_address || mealPlan.pickupAddress || "");
      setPickupInstructions(mealPlan.pickup_instructions || mealPlan.pickupInstructions || "");
      setBasePrice(mealPlan.base_price || mealPlan.basePrice || mealPlan.price || "");
      setDeliveryFee(
        mealPlan.delivery_fee ||
        mealPlan.deliveryFee ||
        mealPlan.deliveryPrice ||
        ""
      );
      setFreeDelivery(
        mealPlan.delivery_fee === 0 ||
        mealPlan.delivery_fee === "0" ||
        mealPlan.deliveryFee === 0 ||
        mealPlan.deliveryFee === "0" ||
        false
      );
      setPickupLatitude(
        mealPlan.pickup_latitude
          ? parseFloat(mealPlan.pickup_latitude)
          : mealPlan.pickupLatitude
            ? parseFloat(mealPlan.pickupLatitude)
            : null
      );
      setPickupLongitude(
        mealPlan.pickup_longitude
          ? parseFloat(mealPlan.pickup_longitude)
          : mealPlan.pickupLongitude
            ? parseFloat(mealPlan.pickupLongitude)
            : null
      );
      setUploadedImage(mealPlan.imageUrl || mealPlan.image_url || null);
    }
  }, [mealPlan, setValue]);

  // Load existing meal items when editing
  useEffect(() => {
    if (mealPlan && mealPlan.items && mealPlan.items.length > 0) {
      console.log("Loading existing meal items:", mealPlan.items);
      // Transform the existing items to match the form's expected format
      const existingItems = mealPlan.items.map((item, index) => ({
        day: index + 1,
        meal: item.meal_name || "",
        description: item.description || "",
        dayOfWeek: convertDayNumberToName(item.day_of_week) || "",
        calories: item.calories || "",
        protein: item.protein || "",
        carbs: item.carbs || "",
        fat: item.fat || "",
        // Handle ingredients - prefer ingredientIds/ingredientsFromTable, fallback to ingredients array
        ingredients:
          item.ingredientIds && item.ingredientIds.length > 0
            ? item.ingredientsFromTable ||
              item.ingredientIds.map((id) => ({ id }))
            : Array.isArray(item.ingredients)
              ? item.ingredients.map((name) => ({
                  name: typeof name === "string" ? name : name.name || "",
                }))
              : item.ingredients
                ? item.ingredients
                    .split(",")
                    .map((ing) => ing.trim())
                    .filter((ing) => ing)
                    .map((name) => ({ name }))
                : [],
        allergens: Array.isArray(item.allergens)
          ? item.allergens.join(", ")
          : item.allergens || "",
      }));
      setMenuItems(existingItems);
    }
  }, [mealPlan]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await uploadAPI.uploadImage(formData);

      if (response.data.success) {
        setUploadedImage(response.data.data.url);
        toast.success("Image uploaded successfully!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const addMenuItem = () => {
    const newDay = Math.max(...menuItems.map((item) => item.day)) + 1;
    setMenuItems([
      ...menuItems,
      {
        day: newDay,
        meal: "",
        description: "",
        ingredients: [], // Changed to array for ingredient objects
        // allergens removed - auto-detected from ingredients
        calories: "",
        protein: "",
        carbs: "",
        fat: "",
      },
    ]);
  };

  const removeMenuItem = (index) => {
    if (menuItems.length > 1) {
      setMenuItems(menuItems.filter((_, i) => i !== index));
    }
  };

  const updateMenuItem = (index, field, value) => {
    const updated = [...menuItems];
    updated[index][field] = value;
    setMenuItems(updated);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser");
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setPickupLatitude(latitude);
        setPickupLongitude(longitude);

        try {
          // Reverse geocoding to get address from coordinates
          const response = await fetch(
            `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=${process.env.REACT_APP_OPENCAGE_API_KEY || "demo"}`
          );
          const data = await response.json();

          if (data.results && data.results.length > 0) {
            const address = data.results[0].formatted;
            setPickupAddress(address);
            toast.success("Location captured successfully!");
          } else {
            setPickupAddress(`${latitude}, ${longitude}`);
            toast.success(
              "Coordinates captured! Please enter address manually."
            );
          }
        } catch (error) {
          console.error("Reverse geocoding failed:", error);
          setPickupAddress(`${latitude}, ${longitude}`);
          toast.success("Coordinates captured! Please enter address manually.");
        }

        setIsGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Unable to get your location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied. Please enable location permissions.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
        }

        toast.error(errorMessage);
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  };

  const onSubmit = async (data) => {
    // Validation: At least one fulfillment method must be selected
    if (!offersDelivery && !offersPickup) {
      toast.error(
        "Please select at least one fulfillment method (Delivery or Pickup)"
      );
      return;
    }

    // Validation: If pickup is selected, address is required
    if (offersPickup && !pickupAddress.trim()) {
      toast.error("Please provide a pickup address");
      return;
    }

    // Validation: Base price is always required
    const basePriceNum = parseFloat(basePrice);
    if (!basePrice || isNaN(basePriceNum) || basePriceNum <= 0) {
      toast.error("Please provide a valid base price for the meal plan");
      return;
    }

    // Validation: Delivery fee handling
    const deliveryFeeNum = freeDelivery ? 0 : parseFloat(deliveryFee);
    if (
      !freeDelivery &&
      (deliveryFee === "" || isNaN(deliveryFeeNum) || deliveryFeeNum < 0)
    ) {
      toast.error("Please provide a valid delivery fee (0 or greater)");
      return;
    }

    // Admin must select a vendor
    if (isAdmin && !vendorId) {
      toast.error("Please select a vendor");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = {
        ...data,
        imageUrl: uploadedImage,
        basePrice: basePriceNum,
        deliveryFee: freeDelivery ? 0 : deliveryFeeNum,
        // For backward compatibility, also send as price
        price: parseFloat(basePrice) || 0,
        // Include vendorId for admin users
        ...(isAdmin && vendorId && { vendorId: parseInt(vendorId) }),
        menuItems: menuItems
          .filter((item) => item.meal.trim() !== "")
          .map((item) => ({
            ...item,
            // Convert string values to appropriate types for backend
            calories: item.calories ? parseInt(item.calories) : null,
            protein: item.protein ? parseFloat(item.protein) : null,
            carbs: item.carbs ? parseFloat(item.carbs) : null,
            fat: item.fat ? parseFloat(item.fat) : null,
            // Handle ingredients - send ingredientIds if available, otherwise fallback to names
            ingredientIds:
              Array.isArray(item.ingredients) && item.ingredients.length > 0
                ? item.ingredients
                    .filter((ing) => ing && typeof ing === "object" && ing.id)
                    .map((ing) => ing.id)
                : undefined,
            // Legacy format: keep ingredients array for backward compatibility
            ingredients:
              Array.isArray(item.ingredients) && item.ingredients.length > 0
                ? item.ingredients
                    .filter((ing) => ing && typeof ing === "object" && ing.name)
                    .map((ing) => ing.name)
                : [],
            // Allergens are now auto-detected from ingredients, don't send them
            allergens: [],
          })),
        offersDelivery,
        offersPickup,
        pickupAddress: offersPickup ? pickupAddress : null,
        pickupLatitude: offersPickup ? pickupLatitude : null,
        pickupLongitude: offersPickup ? pickupLongitude : null,
        pickupInstructions: offersPickup ? pickupInstructions : null,
      };
      await onSave(formData);
    } catch (error) {
      console.error("Error saving meal plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MealPlanFormContainer>
      <MealPlanFormContent>
        <PageDescription>
          <p>
            {mealPlan
              ? isAdmin
                ? "Update meal plan details and menu items"
                : "Update your meal plan details and menu items"
              : isAdmin
                ? "Set up a new meal plan for a vendor with pricing and menu items"
                : "Set up a new meal plan with pricing and menu items"}
          </p>
        </PageDescription>

        <TabNavigation>
          <TabButton
            active={activeTab === "details"}
            onClick={() => setActiveTab("details")}
          >
            📋 Plan Details
          </TabButton>
          <TabButton
            active={activeTab === "menu"}
            onClick={() => setActiveTab("menu")}
          >
            🍽️ Menu Items ({menuItems.length})
          </TabButton>
        </TabNavigation>

        <FormCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <FormHeader>
            <h2>
              {activeTab === "details" ? "Meal Plan Details" : "Menu Items"}
            </h2>
          </FormHeader>

          <FormContent>
            <Form onSubmit={handleSubmit(onSubmit)}>
              {activeTab === "details" && (
                <>
                  {isAdmin && onVendorChange && (
                    <FormGroup>
                      <label htmlFor="vendorId">Vendor *</label>
                      <select
                        id="vendorId"
                        value={vendorId || ""}
                        onChange={(e) => {
                          setVendorId(e.target.value);
                          onVendorChange(e.target.value);
                        }}
                        style={{
                          padding: "0.75rem",
                          border: "2px solid #e0e0e0",
                          borderRadius: "6px",
                          fontSize: "0.875rem",
                          background: "white",
                        }}
                        required
                      >
                        <option value="">Select a vendor</option>
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.businessName}
                          </option>
                        ))}
                      </select>
                      <small style={{ color: "#666", fontSize: "0.875rem" }}>
                        Select the vendor this meal plan belongs to
                      </small>
                    </FormGroup>
                  )}
                  <FormGroup>
                    <label htmlFor="title">Meal Plan Title *</label>
                    <input
                      type="text"
                      id="title"
                      {...register("title")}
                      className={errors.title ? "error" : ""}
                      placeholder="e.g., Weekly Guyanese Delights"
                    />
                    {errors.title && (
                      <div className="error-message">
                        {errors.title.message}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="description">Description *</label>
                    <textarea
                      id="description"
                      {...register("description")}
                      className={errors.description ? "error" : ""}
                      placeholder="Describe your meal plan, what makes it special, etc."
                    />
                    {errors.description && (
                      <div className="error-message">
                        {errors.description.message}
                      </div>
                    )}
                  </FormGroup>

                  <FulfillmentSection>
                    <h3 style={{ marginBottom: "1rem", color: "#2d5016" }}>
                      Fulfillment Options
                    </h3>
                    <p
                      style={{
                        color: "#666",
                        marginBottom: "1rem",
                        fontSize: "0.9rem",
                      }}
                    >
                      Choose how customers can receive their meals
                    </p>

                    <CheckboxGroup>
                      <input
                        type="checkbox"
                        id="offersDelivery"
                        checked={offersDelivery}
                        onChange={(e) => setOffersDelivery(e.target.checked)}
                      />
                      <label htmlFor="offersDelivery">🚚 Offer Delivery</label>
                    </CheckboxGroup>

                    <CheckboxGroup style={{ marginTop: "0.5rem" }}>
                      <input
                        type="checkbox"
                        id="offersPickup"
                        checked={offersPickup}
                        onChange={(e) => setOffersPickup(e.target.checked)}
                      />
                      <label htmlFor="offersPickup">📍 Offer Pickup</label>
                    </CheckboxGroup>

                    {offersPickup && (
                      <div style={{ marginTop: "1rem", paddingLeft: "1rem" }}>
                        <FormGroup>
                          <label htmlFor="pickupAddress">
                            Pickup Address *
                          </label>
                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              alignItems: "flex-start",
                            }}
                          >
                            <input
                              type="text"
                              id="pickupAddress"
                              value={pickupAddress}
                              onChange={(e) => setPickupAddress(e.target.value)}
                              placeholder="Enter pickup location address"
                              style={{ flex: 1 }}
                            />
                            <button
                              type="button"
                              onClick={getCurrentLocation}
                              disabled={isGettingLocation}
                              style={{
                                padding: "0.75rem 1rem",
                                backgroundColor: isGettingLocation
                                  ? "#ccc"
                                  : "#2d5016",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: isGettingLocation
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: "0.875rem",
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isGettingLocation ? (
                                <>
                                  <div
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      border: "2px solid #fff",
                                      borderTop: "2px solid transparent",
                                      borderRadius: "50%",
                                      animation: "spin 1s linear infinite",
                                    }}
                                  />
                                  Getting...
                                </>
                              ) : (
                                <>📍 Use My Location</>
                              )}
                            </button>
                          </div>
                          {pickupLatitude &&
                            pickupLongitude &&
                            typeof pickupLatitude === "number" &&
                            typeof pickupLongitude === "number" && (
                              <small
                                style={{
                                  color: "#666",
                                  fontSize: "0.75rem",
                                  marginTop: "0.25rem",
                                }}
                              >
                                📍 Location: {pickupLatitude.toFixed(6)},{" "}
                                {pickupLongitude.toFixed(6)}
                              </small>
                            )}
                        </FormGroup>
                        <FormGroup style={{ marginTop: "1rem" }}>
                          <label htmlFor="pickupInstructions">
                            Pickup Instructions (Optional)
                          </label>
                          <textarea
                            id="pickupInstructions"
                            value={pickupInstructions}
                            onChange={(e) =>
                              setPickupInstructions(e.target.value)
                            }
                            placeholder="e.g., Call when you arrive, Ring doorbell, etc."
                            rows={3}
                          />
                        </FormGroup>
                      </div>
                    )}
                  </FulfillmentSection>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="basePrice">Base Price (GYD) *</label>
                      <input
                        type="number"
                        step="0.01"
                        id="basePrice"
                        {...register("basePrice")}
                        className={errors.basePrice ? "error" : ""}
                        placeholder="150.00"
                        value={basePrice}
                        onChange={(e) => setBasePrice(e.target.value)}
                      />
                      <small style={{ color: "#666", fontSize: "0.875rem" }}>
                        The base price for the meal plan (pickup price)
                      </small>
                      {errors.basePrice && (
                        <div className="error-message">
                          {errors.basePrice.message}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="deliveryFee">Delivery Fee (GYD) *</label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={freeDelivery}
                            onChange={(e) => {
                              setFreeDelivery(e.target.checked);
                              if (e.target.checked) {
                                setDeliveryFee("0");
                              }
                            }}
                            style={{ margin: 0 }}
                          />
                          <span>Free Delivery</span>
                        </label>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        id="deliveryFee"
                        {...register("deliveryFee")}
                        className={errors.deliveryFee ? "error" : ""}
                        placeholder={freeDelivery ? "0.00" : "25.00"}
                        value={deliveryFee}
                        onChange={(e) => {
                          setDeliveryFee(e.target.value);
                          if (e.target.value === "0" || e.target.value === 0) {
                            setFreeDelivery(true);
                          } else {
                            setFreeDelivery(false);
                          }
                        }}
                        disabled={freeDelivery}
                        style={{
                          opacity: freeDelivery ? 0.6 : 1,
                          backgroundColor: freeDelivery ? "#f5f5f5" : "white",
                        }}
                      />
                      <small style={{ color: "#666", fontSize: "0.875rem" }}>
                        Additional fee for delivery (can be 0 for free delivery)
                      </small>
                      {errors.deliveryFee && (
                        <div className="error-message">
                          {errors.deliveryFee.message}
                        </div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="planType">Plan Type *</label>
                      <select
                        id="planType"
                        {...register("planType")}
                        className={errors.planType ? "error" : ""}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      {errors.planType && (
                        <div className="error-message">
                          {errors.planType.message}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="durationDays">Duration (Days) *</label>
                      <input
                        type="number"
                        id="durationDays"
                        {...register("durationDays")}
                        className={errors.durationDays ? "error" : ""}
                        placeholder="7"
                      />
                      {errors.durationDays && (
                        <div className="error-message">
                          {errors.durationDays.message}
                        </div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <label htmlFor="maxSubscribers">Max Subscribers *</label>
                      <input
                        type="number"
                        id="maxSubscribers"
                        {...register("maxSubscribers")}
                        className={errors.maxSubscribers ? "error" : ""}
                        placeholder="50"
                      />
                      {errors.maxSubscribers && (
                        <div className="error-message">
                          {errors.maxSubscribers.message}
                        </div>
                      )}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <label>Meal Plan Image</label>
                    {uploadedImage ? (
                      <div style={{ marginBottom: "1rem" }}>
                        <img
                          src={uploadedImage}
                          alt="Meal plan preview"
                          style={{
                            width: "100%",
                            maxWidth: "300px",
                            height: "200px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "2px solid #e0e0e0",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedImage(null);
                            document.getElementById("imageUpload").value = "";
                          }}
                          style={{
                            marginTop: "0.5rem",
                            padding: "0.5rem 1rem",
                            background: "#dc3545",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.875rem",
                          }}
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <ImageUpload
                        onClick={() =>
                          document.getElementById("imageUpload").click()
                        }
                        style={{
                          opacity: isUploading ? 0.6 : 1,
                          cursor: isUploading ? "not-allowed" : "pointer",
                        }}
                      >
                        <FiUpload className="upload-icon" />
                        <div className="upload-text">
                          {isUploading
                            ? "Uploading..."
                            : "Click to upload image"}
                        </div>
                        <div className="upload-hint">PNG, JPG up to 5MB</div>
                        <input
                          type="file"
                          id="imageUpload"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={isUploading}
                        />
                      </ImageUpload>
                    )}
                  </FormGroup>
                </>
              )}

              {activeTab === "menu" && (
                <MenuItemsSection>
                  <SectionHeader>
                    <h3>Menu Items</h3>
                    <AddButton type="button" onClick={addMenuItem}>
                      <FiPlus />
                      Add Meal Item
                    </AddButton>
                  </SectionHeader>

                  {menuItems.map((item, index) => (
                    <MenuItem key={index}>
                      <MenuItemHeader>
                        <h4>
                          {item.dayOfWeek
                            ? item.dayOfWeek
                            : `Meal Item ${item.day}`}
                        </h4>
                        <RemoveButton
                          type="button"
                          onClick={() => removeMenuItem(index)}
                          disabled={menuItems.length === 1}
                        >
                          <FiTrash2 />
                        </RemoveButton>
                      </MenuItemHeader>

                      <MenuItemRow>
                        <MenuItemFullRow>
                          <MenuItemLabel>Meal Name *</MenuItemLabel>
                          <MenuItemInput
                            type="text"
                            value={item.meal}
                            onChange={(e) =>
                              updateMenuItem(index, "meal", e.target.value)
                            }
                            placeholder="e.g., Chicken Curry with Rice"
                          />
                        </MenuItemFullRow>
                        <MenuItemFullRow>
                          <MenuItemLabel>Day of Week</MenuItemLabel>
                          <select
                            value={item.dayOfWeek || ""}
                            onChange={(e) =>
                              updateMenuItem(index, "dayOfWeek", e.target.value)
                            }
                            style={{
                              padding: "0.75rem",
                              border: "2px solid #e0e0e0",
                              borderRadius: "6px",
                              fontSize: "0.875rem",
                              background: "white",
                              transition: "border-color 0.3s ease",
                            }}
                          >
                            <option value="">Select day</option>
                            <option value="Monday">Monday</option>
                            <option value="Tuesday">Tuesday</option>
                            <option value="Wednesday">Wednesday</option>
                            <option value="Thursday">Thursday</option>
                            <option value="Friday">Friday</option>
                            <option value="Saturday">Saturday</option>
                            <option value="Sunday">Sunday</option>
                          </select>
                        </MenuItemFullRow>
                      </MenuItemRow>

                      <MenuItemFullRow>
                        <MenuItemLabel>Description</MenuItemLabel>
                        <MenuItemTextarea
                          value={item.description}
                          onChange={(e) =>
                            updateMenuItem(index, "description", e.target.value)
                          }
                          placeholder="Describe the meal, cooking method, etc."
                        />
                      </MenuItemFullRow>

                      <MenuItemFullRow>
                        <MenuItemLabel>Ingredients</MenuItemLabel>
                        <IngredientSearch
                          selectedIngredients={
                            Array.isArray(item.ingredients)
                              ? item.ingredients
                              : item.ingredients
                                ? item.ingredients
                                    .split(",")
                                    .map((ing) => ing.trim())
                                    .filter((ing) => ing)
                                    .map((name) => ({ name }))
                                : []
                          }
                          onIngredientsChange={(ingredients) =>
                            updateMenuItem(index, "ingredients", ingredients)
                          }
                          placeholder="Search and add ingredients..."
                        />
                      </MenuItemFullRow>

                      {/* Allergens are now automatically detected from ingredients */}
                      <MenuItemFullRow>
                        <MenuItemLabel>Allergens</MenuItemLabel>
                        <p
                          style={{
                            padding: "0.75rem",
                            fontSize: "0.875rem",
                            color: "#666",
                            fontStyle: "italic",
                            margin: 0,
                          }}
                        >
                          Allergens will be automatically detected from the
                          ingredients you select.
                        </p>
                      </MenuItemFullRow>

                      <div>
                        <MenuItemLabel>Nutritional Information</MenuItemLabel>
                        <NutritionGrid>
                          <MenuItemFullRow>
                            <MenuItemLabel>Calories</MenuItemLabel>
                            <MenuItemInput
                              type="number"
                              value={item.calories}
                              onChange={(e) =>
                                updateMenuItem(
                                  index,
                                  "calories",
                                  e.target.value
                                )
                              }
                              placeholder="500"
                            />
                          </MenuItemFullRow>
                          <MenuItemFullRow>
                            <MenuItemLabel>Protein (g)</MenuItemLabel>
                            <MenuItemInput
                              type="number"
                              step="0.1"
                              value={item.protein}
                              onChange={(e) =>
                                updateMenuItem(index, "protein", e.target.value)
                              }
                              placeholder="25.5"
                            />
                          </MenuItemFullRow>
                          <MenuItemFullRow>
                            <MenuItemLabel>Carbs (g)</MenuItemLabel>
                            <MenuItemInput
                              type="number"
                              step="0.1"
                              value={item.carbs}
                              onChange={(e) =>
                                updateMenuItem(index, "carbs", e.target.value)
                              }
                              placeholder="45.2"
                            />
                          </MenuItemFullRow>
                          <MenuItemFullRow>
                            <MenuItemLabel>Fat (g)</MenuItemLabel>
                            <MenuItemInput
                              type="number"
                              step="0.1"
                              value={item.fat}
                              onChange={(e) =>
                                updateMenuItem(index, "fat", e.target.value)
                              }
                              placeholder="12.8"
                            />
                          </MenuItemFullRow>
                        </NutritionGrid>
                      </div>
                    </MenuItem>
                  ))}
                </MenuItemsSection>
              )}
            </Form>
          </FormContent>

          <FormFooter>
            <Button type="button" onClick={handleCancel}>
              <FiX />
              Cancel
            </Button>

            {activeTab === "details" && (
              <Button
                type="button"
                onClick={() => setActiveTab("menu")}
                style={{ background: "#6c757d" }}
              >
                Next: Menu Items →
              </Button>
            )}

            {activeTab === "menu" && (
              <Button
                type="button"
                onClick={() => setActiveTab("details")}
                style={{ background: "#6c757d" }}
              >
                ← Back to Details
              </Button>
            )}

            <Button
              type="submit"
              $primary
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              <FiSave />
              {isSubmitting
                ? "Saving..."
                : mealPlan
                  ? "Update Plan"
                  : "Create Plan"}
            </Button>
          </FormFooter>
        </FormCard>
      </MealPlanFormContent>
    </MealPlanFormContainer>
  );
};

export default MealPlanForm;
